<template>
  <div class="c-quill-editor">
    <quill-editor
      ref="myTextEditor"
      v-model="data"
      :disabled="disabled"
      :options="editorOption"
      :style="`height:${option.editorHeight || 450}px;`"
      @ready="onEditorReady"
    />
  </div>
</template>

<script>
import common from '@/api/common/index'
import { quillEditor } from 'vue-quill-editor'
import { base64ToBlob } from '@/util/func'

import '@/assets/scss/quill/quill.core.css'
import '@/assets/scss/quill/quill.snow.css'
import '@/assets/scss/quill/quill.bubble.css'

const IMAGE_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/x-icon',
  'image/svg+xml',
]

export default {
  components: {
    quillEditor,
  },
  props: {
    value: {
      type: [String, Number, Object, Array, Boolean],
      default: undefined,
    },
    option: {
      type: Object,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      data: undefined,
      quill: null,
      replacingBase64: false,
      savedRange: { index: 0, length: 0 },
    }
  },
  computed: {
    editorOption() {
      const option = {
        placeholder: this.$t('_components.message.editInfo'),
        modules: {
          toolbar: {
            container: [
              ['bold', 'italic', 'underline', 'strike'],
              [{ size: ['small', 'normal', 'large', 'huge'] }][
                ({ header: 1 }, { header: 2 })
              ],
              [{ list: 'ordered' }, { list: 'bullet' }],
              [{ size: ['small', false, 'large', 'huge'] }],
              [{ indent: '-1' }, { indent: '+1' }],
              [{ direction: 'rtl' }],
              [{ font: [] }],

              [{ color: [] }, { background: [] }],
              [{ align: [] }],

              ['clean', 'link', 'image'],
            ],
            handlers: {
              image: this.selectLocalImage,
            },
          },
        },
      }
      return option
    },
  },
  watch: {
    data: {
      deep: true,
      handler(e) {
        this.$emit('input', e)
      },
    },
    value: {
      handler(e) {
        this.data = e
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    this.syncEditorHeight()
  },
  beforeDestroy() {
    this.unbindEditorEvents()
  },
  methods: {
    onEditorReady(quill) {
      if (this.quill && this.quill !== quill) {
        this.unbindEditorEvents()
      }
      this.quill = quill
      this.bindEditorEvents()
      this.syncEditorHeight()
    },
    syncEditorHeight() {
      const editor = this.$refs.myTextEditor
      if (!editor || !editor.$el) return
      const editorToolbarDom = editor.$el.querySelector('.ql-toolbar')
      const editorContainerDom = editor.$el.querySelector('.ql-container')
      if (!editorToolbarDom || !editorContainerDom) return
      const hei = this.option.editorHeight || 450
      editorContainerDom.style.height = `${hei - editorToolbarDom.offsetHeight}px`
    },
    bindEditorEvents() {
      const { quill } = this
      if (!quill) return

      const uploader = quill.getModule('uploader')
      if (uploader) {
        uploader.options.mimetypes = IMAGE_MIME_TYPES
        uploader.options.handler = (range, files) => {
          this.uploadFiles(range, files)
        }
      }

      quill.root.addEventListener('paste', this.handleImagePaste, true)
      quill.root.addEventListener('drop', this.handleImageDrop, true)
      quill.on('text-change', this.handleTextChange)
    },
    unbindEditorEvents() {
      const { quill } = this
      if (!quill || !quill.root) return
      quill.root.removeEventListener('paste', this.handleImagePaste, true)
      quill.root.removeEventListener('drop', this.handleImageDrop, true)
      quill.off('text-change', this.handleTextChange)
    },
    getImageFiles(fileList) {
      return Array.from(fileList || []).filter((file) => {
        if (!file) return false
        return IMAGE_MIME_TYPES.includes(file.type) || String(file.type || '').indexOf('image/') === 0
      })
    },
    getInsertIndex(range) {
      const { quill } = this
      if (range && typeof range.index === 'number') return range.index
      const selection = quill.getSelection(true)
      if (selection && typeof selection.index === 'number') return selection.index
      return Math.max(quill.getLength() - 1, 0)
    },
    async uploadFile(file) {
      const response = await common.upload('file', file)
      const url = response && response.data && response.data.data && response.data.data.link
      if (!url) {
        throw new Error('upload image failed')
      }
      return url
    },
    async uploadFiles(range, files) {
      const { quill } = this
      if (!quill) return
      const imageFiles = this.getImageFiles(files)
      if (!imageFiles.length) return
      let index = this.getInsertIndex(range)
      for (let i = 0; i < imageFiles.length; i += 1) {
        try {
          const url = await this.uploadFile(imageFiles[i])
          quill.insertEmbed(index, 'image', url, 'user')
          index += 1
          quill.setSelection(index, 0, 'silent')
        } catch (err) {
          console.error(err)
        }
      }
    },
    selectLocalImage() {
      const { quill } = this
      if (!quill) return
      this.savedRange = quill.getSelection(true) || { index: this.getInsertIndex(), length: 0 }
      const input = document.createElement('input')
      input.setAttribute('type', 'file')
      input.setAttribute('accept', 'image/*')
      input.click()

      input.onchange = () => {
        const file = input.files && input.files[0]
        if (!file) return
        this.uploadFiles(this.savedRange, [file])
      }
    },
    handleImagePaste(e) {
      if (this.disabled) return
      const clipboard = e.clipboardData
      if (!clipboard) return
      const html = clipboard.getData('text/html') || ''
      const files = this.getImageFiles(clipboard.files)
      const htmlHasDataImage = /src\s*=\s*["']data:image/i.test(html)
      const htmlHasRemoteImage = /<img[^>]+src\s*=\s*["'](?!data:|blob:)[^"']+/i.test(html)

      if (files.length && !htmlHasDataImage && !htmlHasRemoteImage) {
        e.preventDefault()
        e.stopPropagation()
        if (typeof e.stopImmediatePropagation === 'function') {
          e.stopImmediatePropagation()
        }
        const range = this.quill.getSelection(true) || { index: this.getInsertIndex(), length: 0 }
        this.uploadFiles(range, files)
      }
    },
    handleImageDrop(e) {
      if (this.disabled) return
      const files = this.getImageFiles(e.dataTransfer && e.dataTransfer.files)
      if (!files.length) return
      e.preventDefault()
      e.stopPropagation()
      if (typeof e.stopImmediatePropagation === 'function') {
        e.stopImmediatePropagation()
      }
      const range = this.quill.getSelection(true) || { index: this.getInsertIndex(), length: 0 }
      this.uploadFiles(range, files)
    },
    handleTextChange(delta, oldDelta, source) {
      if (source !== 'user' || this.replacingBase64) return
      const hasBase64 = (delta.ops || []).some(
        (op) => op.insert && op.insert.image && String(op.insert.image).indexOf('data:image') === 0
      )
      if (!hasBase64) return
      this.replaceBase64Images()
    },
    collectBase64Images() {
      const { quill } = this
      const contents = quill.getContents()
      let index = 0
      const items = []
      ;(contents.ops || []).forEach((op) => {
        const { insert } = op
        const length = typeof insert === 'string' ? insert.length : 1
        if (insert && insert.image && String(insert.image).indexOf('data:image') === 0) {
          items.push({ index, src: insert.image })
        }
        index += length
      })
      return items
    },
    dataUrlToFile(dataUrl) {
      const blob = base64ToBlob(dataUrl)
      const mime = blob.type || 'image/png'
      const ext = (mime.split('/')[1] || 'png').split('+')[0]
      return new File([blob], `image.${ext}`, { type: mime })
    },
    async replaceBase64Images() {
      const { quill } = this
      if (!quill || this.replacingBase64) return
      const items = this.collectBase64Images()
      if (!items.length) return
      this.replacingBase64 = true
      try {
        for (let i = items.length - 1; i >= 0; i -= 1) {
          const item = items[i]
          try {
            const file = this.dataUrlToFile(item.src)
            const url = await this.uploadFile(file)
            quill.deleteText(item.index, 1, 'silent')
            quill.insertEmbed(item.index, 'image', url, 'silent')
          } catch (err) {
            console.error(err)
          }
        }
        this.data = quill.root.innerHTML
      } finally {
        this.replacingBase64 = false
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
